/**
 * The one form for adding and editing an entry. In edit mode on a feed-owned
 * entry the feed's fields are disabled with a line saying who owns them, per
 * SPEC: a refresh would overwrite anything typed there anyway.
 *
 * It is the fallback, not the product, so it is shaped for the common case: a
 * name and a date, then the kind — a birthday unless said otherwise — and the
 * circles. Colour and weight are behind "Appearance", because they are the
 * circles' vocabulary and most entries never need them.
 */
import { MONTH_NAMES, createEventSchema, daysInMonth, type CalEvent, type Source, type StyleTokens, type Tag } from '@circle/shared';
import { api, type CreateEventBody, type PatchEventBody } from '../app/api';
import { validate } from '../app/validate';
import { el, selectField, textField, withBusy, type Field } from './dom';
import { PhotoPicker } from './photo-picker';
import { TagChips } from './tag-chips';
import {
  PROMINENCE_STEPS,
  formatProminence,
  prominenceFromSlider,
  prominenceToSlider,
} from './circles';
import { toastError } from './toast';

export interface EventFormOptions {
  calendarId: string;
  /** Editing an existing entry, or undefined when adding. */
  event?: CalEvent;
  /** The entry's source, used to decide which fields the feed owns. */
  source?: Source | undefined;
  /** Pre-filled date when adding from a click on a day. */
  date?: { month: number; day: number };
  viewYear: number;
  /** The calendar's circles, read live. Absent: no circle field at all. */
  tags?: () => Tag[];
  /** A circle was made from the chip row; the page refetches the bundle. */
  onTagsChanged?: () => Promise<void> | void;
  onSaved: (event: CalEvent) => void;
  onCancel?: () => void;
}

const KINDS = [
  { value: 'birthday', label: 'Birthday' },
  { value: 'note', label: 'Note' },
];

/**
 * Only a feed has an upstream that would overwrite the name and the date. A
 * collect entry was typed once by the person it is about, and a typo in it is
 * the owner's to fix; a manual one is the owner's outright.
 */
export function isFeedOwned(source: Source | undefined): boolean {
  return source?.kind === 'ics';
}

/**
 * The style to send: the entry's other tokens kept, the colour set or cleared.
 * The worker replaces `style_json` wholesale, so sending only the colour would
 * quietly drop a mark or a card variant set elsewhere.
 */
export function mergedStyle(current: StyleTokens | undefined, colour: string | undefined): StyleTokens {
  const { colour: _dropped, ...rest } = current ?? {};
  return colour ? { ...rest, colour } : rest;
}

export class EventForm {
  readonly root: HTMLFormElement;
  private readonly options: EventFormOptions;
  private readonly kind: Field<HTMLSelectElement>;
  private readonly title: Field<HTMLInputElement>;
  private readonly month: Field<HTMLSelectElement>;
  private readonly day: Field<HTMLSelectElement>;
  private readonly year: Field<HTMLInputElement>;
  private readonly colour: HTMLInputElement;
  private readonly photo: PhotoPicker;
  private readonly submit: HTMLButtonElement;
  private readonly summary: HTMLElement;
  private readonly chips: TagChips | undefined;
  private readonly weight: HTMLInputElement;
  /** What the entry's circles were when the form opened, to spot a change. */
  private readonly tagsAtOpen: string[];

  constructor(options: EventFormOptions) {
    this.options = options;
    const event = options.event;
    const feedOwned = isFeedOwned(options.source);

    this.kind = selectField({
      label: 'Kind',
      name: 'kind',
      options: KINDS,
      value: event?.kind ?? 'birthday',
      hint: 'A birthday shows an age; a note is anything else that happens on a day.',
    });
    this.title = textField({
      label: 'Name',
      name: 'title',
      value: event?.title ?? '',
      placeholder: 'Anna Lindqvist',
      maxlength: '120',
      required: true,
    });
    this.month = selectField({
      label: 'Month',
      name: 'month',
      options: MONTH_NAMES.map((m, i) => ({ value: String(i + 1), label: m })),
      value: String(event?.month ?? options.date?.month ?? new Date().getMonth() + 1),
    });
    this.day = selectField({ label: 'Day', name: 'day', options: [] });
    this.year = textField({
      label: 'Year',
      name: 'year',
      inputmode: 'numeric',
      placeholder: '1986',
      maxlength: '4',
      value: event?.year !== undefined ? String(event.year) : '',
    });

    this.colour = el('input', {
      type: 'color',
      value: event?.style?.colour ?? '#d8b26a',
      'aria-label': 'Colour',
    });
    const colourOn = el('input', {
      type: 'checkbox',
      checked: Boolean(event?.style?.colour),
      'aria-label': 'Use a custom colour',
    });
    colourOn.addEventListener('change', () => (this.colour.disabled = !colourOn.checked));
    this.colour.disabled = !colourOn.checked;

    this.photo = new PhotoPicker({
      imageKey: event?.imageKey,
      upload: (file) => api.uploadImage(file, options.calendarId),
    });

    // Circles are not a feed's business: an ICS refresh owns the name and the
    // date and nothing else, so these stay live on a feed-owned entry.
    this.tagsAtOpen = [...(event?.tagIds ?? [])];
    this.chips = options.tags
      ? new TagChips({
          tags: options.tags,
          selected: this.tagsAtOpen,
          onCreate: async (name) => {
            try {
              const created = await api.createTag(options.calendarId, { name });
              await options.onTagsChanged?.();
              this.chips?.paint();
              return created;
            } catch (err) {
              toastError(err, 'That circle was not made.');
              return undefined;
            }
          },
          emptyText: 'No circles yet — make one to group people.',
        })
      : undefined;

    this.weight = el('input', {
      type: 'range',
      class: 'circle__prom',
      min: '0',
      max: String(PROMINENCE_STEPS),
      step: '1',
      value: String(prominenceToSlider(event?.prominence ?? 1)),
      'aria-label': 'Weight of this person',
    });
    const weightValue = el('span', {
      class: 'circle__prom-value mono',
      text: formatProminence(event?.prominence ?? 1),
    });
    this.weight.addEventListener('input', () => {
      weightValue.textContent = formatProminence(prominenceFromSlider(Number(this.weight.value)));
    });

    this.summary = el('div', { class: 'field__error', role: 'alert' });
    this.summary.hidden = true;

    this.submit = el('button', {
      class: 'btn btn--primary',
      type: 'submit',
      text: event ? 'Save changes' : 'Add to the ring',
    });

    const cancel = options.onCancel
      ? el('button', { class: 'btn', type: 'button', text: 'Cancel', onClick: options.onCancel })
      : null;

    // Open when something in it is already set, so an edit does not hide the
    // very thing being edited; shut otherwise, which is most of the time.
    const appearance = el(
      'details',
      { class: 'form-disclosure', open: Boolean(event?.style?.colour) || (event?.prominence ?? 1) !== 1 },
      el('summary', { text: 'Appearance' }),
      el(
        'div',
        { class: 'stack form-disclosure__body' },
        el(
          'div',
          { class: 'field' },
          el('span', { class: 'muted small', text: 'Colour' }),
          el(
            'label',
            { class: 'row small muted' },
            colourOn,
            el('span', { text: 'Override the source colour' }),
            this.colour,
          ),
        ),
        el(
          'div',
          { class: 'field' },
          el('span', { class: 'muted small', text: 'Weight' }),
          el('div', { class: 'circle__weight' }, this.weight, weightValue),
          el('div', {
            class: 'field__hint',
            text: 'Bigger on the ring, and first out of a crowded week. Leave it in the middle for most people.',
          }),
        ),
      ),
    );

    this.root = el(
      'form',
      { class: 'stack', novalidate: true },
      feedOwned
        ? el('div', {
            class: 'notice',
            text: `Name and date come from “${options.source?.name}”. Editing them here would be undone at the next refresh, so change them at the source. Photo and colour are yours to set.`,
          })
        : null,
      this.title.root,
      el('div', { class: 'field-row field-row--date' }, this.month.root, this.day.root, this.year.root),
      this.kind.root,
      this.chips
        ? el(
            'div',
            { class: 'field' },
            el('span', { class: 'muted small', text: 'Circles' }),
            this.chips.root,
          )
        : null,
      this.photo.root,
      appearance,
      this.summary,
      el('div', { class: 'form-actions' }, el('span', { class: 'grow' }), cancel, this.submit),
    );

    this.kind.input.addEventListener('change', () => this.paintYearHint());
    this.month.input.addEventListener('change', () => this.fillDays());
    this.fillDays(event?.day ?? options.date?.day);
    this.paintYearHint();

    if (feedOwned) {
      for (const field of [this.kind, this.title, this.month, this.day, this.year]) {
        field.input.disabled = true;
      }
    }

    this.root.addEventListener('submit', (e) => {
      e.preventDefault();
      void this.save();
    });
  }

  focus(): void {
    this.title.input.focus();
  }

  destroy(): void {
    this.photo.destroy();
  }

  /** Day options depend on the month; 29 Feb stays available (it folds to the 28th). */
  private fillDays(selected?: number): void {
    const month = Number(this.month.input.value) || 1;
    const previous = selected ?? (Number(this.day.input.value) || 1);
    const count = daysInMonth(2024, month); // a leap year, so 29 Feb is offerable
    this.day.input.replaceChildren(
      ...Array.from({ length: count }, (_, i) =>
        el('option', { value: String(i + 1), text: String(i + 1) }),
      ),
    );
    this.day.input.value = String(Math.min(previous, count));
  }

  private paintYearHint(): void {
    const birthday = this.kind.input.value === 'birthday';
    const hint = this.year.root.querySelector('.field__hint');
    const text = birthday
      ? 'Birth year. Leave it out and the ring shows the name only.'
      : 'Leave it out for something that repeats every year.';
    if (hint) hint.textContent = text;
    else this.year.input.insertAdjacentElement('afterend', el('div', { class: 'field__hint', text }));
  }

  private read(): { body: CreateEventBody; yearCleared: boolean } {
    const rawYear = this.year.input.value.trim();
    const year = rawYear ? Number(rawYear) : undefined;
    const colour = this.colour.disabled ? undefined : this.colour.value;
    const style = mergedStyle(this.options.event?.style, colour);
    return {
      body: {
        kind: this.kind.input.value as CalEvent['kind'],
        title: this.title.input.value.trim(),
        month: Number(this.month.input.value),
        day: Number(this.day.input.value),
        ...(year !== undefined && Number.isFinite(year) ? { year } : {}),
        ...(this.photo.value ? { imageKey: this.photo.value } : {}),
        ...(Object.keys(style).length ? { style } : {}),
        prominence: prominenceFromSlider(Number(this.weight.value)),
      },
      yearCleared: rawYear === '',
    };
  }

  private setErrors(errors: Record<string, string>): void {
    for (const [field, control] of Object.entries({
      title: this.title,
      month: this.month,
      day: this.day,
      year: this.year,
      kind: this.kind,
    })) {
      control.setError(errors[field] ?? null);
    }
  }

  private async save(): Promise<void> {
    this.summary.hidden = true;
    const { body, yearCleared } = this.read();
    const result = validate(createEventSchema, body);
    if (!result.ok) {
      this.setErrors(result.errors);
      this.summary.textContent = result.message;
      this.summary.hidden = false;
      return;
    }
    this.setErrors({});

    const { calendarId, event, source } = this.options;
    await withBusy(this.submit, async () => {
      try {
        if (!event) {
          const created = await api.createEvent(calendarId, result.data);
          this.options.onSaved(await this.saveTags(calendarId, created));
          return;
        }
        // Always the merged style, never `{}`: the worker replaces the whole
        // of `style_json`, and an empty object would wipe tokens this form has
        // no control for.
        const style = result.data.style ?? {};
        const patch: PatchEventBody = isFeedOwned(source)
          ? { imageKey: this.photo.value, prominence: result.data.prominence, style }
          : {
              ...result.data,
              year: yearCleared ? null : (result.data.year ?? null),
              imageKey: this.photo.value,
              style,
            };
        const saved = await api.patchEvent(calendarId, event.id, patch);
        this.options.onSaved(await this.saveTags(calendarId, saved));
      } catch (err) {
        toastError(err);
      }
    });
  }

  /**
   * The circles go in a second request — `PUT /api/events/:id/tags` replaces the
   * whole set — so they are sent only when they actually moved, and a new entry
   * with no circles costs nothing extra.
   */
  private async saveTags(calendarId: string, saved: CalEvent): Promise<CalEvent> {
    if (!this.chips) return saved;
    const next = this.chips.value;
    const before = [...this.tagsAtOpen].sort();
    const after = [...next].sort();
    if (after.length === before.length && after.every((id, i) => id === before[i])) return saved;
    return await api.setEventTags(calendarId, saved.id, next);
  }
}
